import React, { useState, useMemo, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Languages, Search, X, Check, Plus } from 'lucide-react';
import s from './AddLanguageModal.module.sass';
import { getAvailableLanguages } from '../../config/languages';
import FlagIcon from '../FlagIcon/FlagIcon';

// Emoji-флаг из справочника языков → код страны для FlagIcon (🇪🇸 → 'es')
const emojiFlagToCode = (emoji) => {
    const m = (emoji || '').match(/[\u{1F1E6}-\u{1F1FF}]{2}/u);
    if (!m) return null;
    return Array.from(m[0])
        .map((c) => String.fromCodePoint(c.codePointAt(0) - 0x1F1E6 + 97))
        .join('');
};

const AddLanguageModal = ({ isOpen, onClose, onAddLanguage, existingLanguages = [], nova = false }) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedLanguage, setSelectedLanguage] = useState(null);
    const searchRef = useRef(null);

    // Получаем доступные языки (исключая уже добавленные)
    const availableLanguages = useMemo(() => {
        return getAvailableLanguages(existingLanguages);
    }, [existingLanguages]);

    // Фильтруем языки по поисковому запросу
    const filteredLanguages = useMemo(() => {
        if (!searchTerm.trim()) return availableLanguages;

        const term = searchTerm.toLowerCase();
        return availableLanguages.filter(lang =>
            lang.name.toLowerCase().includes(term) ||
            lang.nativeName.toLowerCase().includes(term) ||
            lang.code.toLowerCase().includes(term)
        );
    }, [availableLanguages, searchTerm]);

    const handleAddLanguage = () => {
        if (selectedLanguage) {
            onAddLanguage(selectedLanguage);
            onClose();
            setSelectedLanguage(null);
            setSearchTerm('');
        }
    };

    const handleClose = () => {
        onClose();
        setSelectedLanguage(null);
        setSearchTerm('');
    };

    // Esc — закрыть, Enter — добавить выбранный
    useEffect(() => {
        if (!isOpen || !nova) return;
        const onKey = (e) => {
            if (e.key === 'Escape') handleClose();
            if (e.key === 'Enter' && selectedLanguage) handleAddLanguage();
        };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, nova, selectedLanguage]);

    if (!isOpen) return null;

    // ── Nova: своя вёрстка — карточка в токенах темы, наши флаги вместо эмодзи
    if (nova) {
        const novaRoot = document.querySelector('[data-nova-theme]') || document.body;
        return createPortal(
            <div className={s.nvOverlay} onClick={handleClose}>
                <div
                    className={s.nvCard}
                    role="dialog"
                    aria-modal="true"
                    aria-label="Добавить язык"
                    onClick={(e) => e.stopPropagation()}
                >
                    <div className={s.nvHead}>
                        <span className={s.nvHeadGlyph}>
                            <Languages size={15} strokeWidth={2.2} aria-hidden="true" />
                        </span>
                        <div className={s.nvHeadText}>
                            <div className={s.nvTitle}>Добавить язык</div>
                            <div className={s.nvSubtitle}>Новый язык появится во всех переводах сайта</div>
                        </div>
                        <button type="button" className={s.nvClose} onClick={handleClose} aria-label="Закрыть">
                            <X size={16} strokeWidth={2.2} aria-hidden="true" />
                        </button>
                    </div>

                    <div className={s.nvSearch}>
                        <Search size={15} strokeWidth={2} aria-hidden="true" />
                        <input
                            ref={searchRef}
                            type="text"
                            placeholder="Название, родное имя или код…"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            spellCheck="false"
                            autoFocus
                        />
                        {searchTerm && (
                            <button type="button" className={s.nvSearchClear} onClick={() => setSearchTerm('')} aria-label="Очистить">
                                <X size={12} strokeWidth={2.6} aria-hidden="true" />
                            </button>
                        )}
                    </div>

                    <div className={s.nvList}>
                        {filteredLanguages.length > 0 ? (
                            filteredLanguages.map((language) => {
                                const active = selectedLanguage?.code === language.code;
                                return (
                                    <button
                                        type="button"
                                        key={language.code}
                                        className={`${s.nvRow} ${active ? s.nvRowActive : ''}`}
                                        onClick={() => setSelectedLanguage(active ? null : language)}
                                        onDoubleClick={handleAddLanguage}
                                    >
                                        <FlagIcon code={emojiFlagToCode(language.flag) || 'unknown'} className={s.nvFlag} alt="" />
                                        <span className={s.nvRowText}>
                                            <span className={s.nvRowName}>{language.name}</span>
                                            <span className={s.nvRowNative}>{language.nativeName}</span>
                                        </span>
                                        <span className={s.nvRowCode}>{language.code.toUpperCase()}</span>
                                        <span className={`${s.nvRowCheck} ${active ? s.nvRowCheckOn : ''}`}>
                                            <Check size={13} strokeWidth={2.8} aria-hidden="true" />
                                        </span>
                                    </button>
                                );
                            })
                        ) : (
                            <div className={s.nvEmpty}>
                                <Search size={20} strokeWidth={1.8} aria-hidden="true" />
                                <span>Ничего не нашлось по «{searchTerm}»</span>
                            </div>
                        )}
                    </div>

                    <div className={s.nvFoot}>
                        <button type="button" className={s.nvCancel} onClick={handleClose}>
                            Отмена
                        </button>
                        <button
                            type="button"
                            className={s.nvAdd}
                            onClick={handleAddLanguage}
                            disabled={!selectedLanguage}
                        >
                            <Plus size={14} strokeWidth={2.6} aria-hidden="true" />
                            {selectedLanguage
                                ? `Добавить ${selectedLanguage.code.toUpperCase()}`
                                : 'Добавить язык'}
                        </button>
                    </div>
                </div>
            </div>,
            novaRoot
        );
    }

    return (
        <div className={s.modalOverlay} onClick={handleClose}>
            <div className={s.modalContent} onClick={(e) => e.stopPropagation()}>
                <div className={s.modalHeader}>
                    <h3 className={s.modalTitle}>
                        🌍 Добавить новый язык
                    </h3>
                    <button className={s.closeButton} onClick={handleClose}>
                        ✕
                    </button>
                </div>

                <div className={s.modalBody}>
                    {/* Поиск */}
                    <div className={s.searchContainer}>
                        <input
                            type="text"
                            className={s.searchInput}
                            placeholder="🔍 Поиск языка..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            autoFocus
                        />
                    </div>

                    {/* Список языков */}
                    <div className={s.languagesList}>
                        {filteredLanguages.length > 0 ? (
                            filteredLanguages.map((language) => (
                                <div
                                    key={language.code}
                                    className={`${s.languageItem} ${
                                        selectedLanguage?.code === language.code ? s.selected : ''
                                    }`}
                                    onClick={() => setSelectedLanguage(language)}
                                >
                                    <div className={s.languageFlag}>
                                        {language.flag}
                                    </div>
                                    <div className={s.languageInfo}>
                                        <div className={s.languageName}>
                                            {language.name}
                                        </div>
                                        <div className={s.languageNative}>
                                            {language.nativeName}
                                        </div>
                                    </div>
                                    <div className={s.languageCode}>
                                        {language.code.toUpperCase()}
                                    </div>
                                </div>
                            ))
                        ) : (
                            <div className={s.noResults}>
                                <div className={s.noResultsIcon}>🔍</div>
                                <div className={s.noResultsText}>
                                    Языки не найдены
                                </div>
                                <div className={s.noResultsSubtext}>
                                    Попробуйте изменить поисковый запрос
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Информация о выбранном языке */}
                    {selectedLanguage && (
                        <div className={s.selectedLanguageInfo}>
                            <div className={s.selectedLabel}>Выбранный язык:</div>
                            <div className={s.selectedLanguage}>
                                <span className={s.selectedFlag}>
                                    {selectedLanguage.flag}
                                </span>
                                <span className={s.selectedName}>
                                    {selectedLanguage.name} ({selectedLanguage.nativeName})
                                </span>
                                <span className={s.selectedCode}>
                                    [{selectedLanguage.code.toUpperCase()}]
                                </span>
                            </div>
                        </div>
                    )}
                </div>

                <div className={s.modalFooter}>
                    <button className={s.cancelButton} onClick={handleClose}>
                        Отмена
                    </button>
                    <button 
                        className={s.addButton} 
                        onClick={handleAddLanguage}
                        disabled={!selectedLanguage}
                    >
                        Добавить язык
                    </button>
                </div>
            </div>
        </div>
    );
};

export default AddLanguageModal;
